/**
 * Projectile flight visualizer for Flamecaller's "Shoot a projectile"
 * ability (and any future direction-based projectile ability).
 *
 * Two visual layers:
 *   • Trail halos. Every cell the projectile crosses except the
 *     impact cell. Faint orange so the player reads "this is the
 *     flight path" without confusing it for a clickable target.
 *   • Impact halos. The cell the projectile lands on, per cardinal
 *     direction. Bright red/orange so the player reads "this is what
 *     the projectile actually hits".
 *
 * One DOM node per cell. Position + visibility mutated each frame by
 * GameBoard.updateProjectilePathOverlays so cell-quad projection stays
 * in sync with camera pan / zoom. React only re-renders when the
 * `paths` identity changes.
 *
 * Hover-by-direction: while the cursor sits inside any cell of a
 * direction's trail (or its impact), every halo in that direction is
 * marked data-hovered="true" so CSS can brighten the whole line. The
 * player gets a clear "shooting this way" preview as they move the
 * cursor.
 */
export default function ProjectilePathOverlays({ paths, registerRefs }) {
  if (!paths || paths.length === 0) return null;

  return (
    <div aria-hidden="true">
      {paths.map((path) =>
        path.pathCells.map(({ col, row }) => {
          const key = `${col},${row}`;
          const isImpact = path.impactCell?.col === col && path.impactCell?.row === row;
          const refKey = `${path.direction}|${key}`;
          return (
            <Halo
              key={refKey}
              refKey={refKey}
              direction={path.direction}
              kind={isImpact ? 'impact' : 'trail'}
              registerRefs={registerRefs}
            />
          );
        }),
      )}
    </div>
  );
}

function Halo({ refKey, direction, kind, registerRefs }) {
  const setHalo = (el) => registerRefs(refKey, el);
  const className =
    kind === 'impact' ? 'projectile-impact-halo' : 'projectile-trail-halo';
  return <div ref={setHalo} className={className} data-direction={direction} />;
}
